import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import { ShellService } from '@app/shell/services/shell.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isMobileView, SetPushNotificationSeen } from '@core/utils';
import { NotificationsComposition } from '@shared/compositions';
import { Router } from '@angular/router';
import { DialogService } from '@ngneat/dialog';
import { PushNotificationsComponent } from '@shared/components';
import { UsePushNotification } from '@core/usecases';
import { SocketIoService } from '@core/socket-io';
import { SocketEvents } from '@core/enums';
import { Logger, SessionDeletionService } from '@core/services';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';

const log = new Logger('Shell');

@UntilDestroy()
@Component({
  selector: 'app-shell',
  templateUrl: './shell.component.html',
  standalone: false,
})
export class ShellComponent implements OnInit, AfterViewInit {
  isMobileView = isMobileView();
  isSidebarActive = false;
  private readonly _shellService = inject(ShellService);
  private readonly _notificationsComposition = inject(NotificationsComposition);
  private readonly _router = inject(Router);
  private readonly _dialogService = inject(DialogService);
  private readonly _bottomSheetService = inject(NgxBottomSheetService);
  private readonly _socketService = inject(SocketIoService);
  private readonly _sessionDeletionService = inject(SessionDeletionService);
  private readonly _usePushNotification = new UsePushNotification();

  ngOnInit() {
    if (this._sessionDeletionService.checkCurrentSession()) {
      return;
    }

    this._shellService.isMobileView$.pipe(untilDestroyed(this)).subscribe({
      next: (isMobileView) => {
        this.isMobileView = isMobileView;
      },
    });

    this._shellService.isMobileView$.pipe(untilDestroyed(this)).subscribe({
      next: (isMobileView) => {
        this._redirectBasedOnView(isMobileView);
      },
    });

    this._notificationsComposition.fetchNotifications();

    this._socketService.connect();

    this._socketService
      .fromEvent(SocketEvents.ForceLogout)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data) => {
          log.info('System Requested Logout');
          this._router.navigate(['/logout']);
        },
      });
  }

  ngAfterViewInit() {
    if (this._shellService._permissionService.isMemberLight() || this._shellService._permissionService.isMemberLightAdmin()) return;
    this._fetchPushNotifications();
  }

  sidebarToggle(toggleState: boolean) {
    this.isSidebarActive = toggleState;
  }

  private _redirectBasedOnView(isMobileView: boolean): void {
    const currentRoute = this._router.url;
    let promise$;

    if (isMobileView && !currentRoute.startsWith('/m/')) {
      promise$ = this._router.navigate([`/m${currentRoute}`]);
    } else if (!isMobileView && currentRoute.startsWith('/m/')) {
      promise$ = this._router.navigate([currentRoute.slice(2)]);
    }
    if (promise$) {
      promise$.then(() => {
        this._dialogService.closeAll();
        this._bottomSheetService.closeAll();
      });
    }
  }

  private _fetchPushNotifications() {
    //TODO: temporary omitting push notifications for mobile view
    if (isMobileView()) return;

    this._usePushNotification
      .getAllNotifications()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (notifications) => {
          if (
            (notifications.surveyNotifications && notifications.surveyNotifications.length) ||
            (notifications.crmNewsNotifications && notifications.crmNewsNotifications.length) ||
            (notifications.crmSurveyNotifications && notifications.crmSurveyNotifications.length)
          ) {
            SetPushNotificationSeen();
            if (!isMobileView()) {
              this._dialogService
                .open(PushNotificationsComponent, {
                  size: 'md',
                  closeButton: false,
                  enableClose: false,
                  windowClass: 'dialog-push-notification',
                  data: {
                    surveyNotifications: notifications.surveyNotifications,
                    crmNewsNotifications: notifications.crmNewsNotifications,
                    crmSurveyNotifications: notifications.crmSurveyNotifications,
                  },
                })
                .afterClosed$.pipe(untilDestroyed(this))
                .subscribe({
                  next: (data) => {
                    if (!data) return;
                    this._reloadCurrentRoute(data);
                  },
                });
            }
          }
        },
      });
  }

  private _reloadCurrentRoute(path?: string) {
    const currentUrl = path || this._router.url;
    this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this._router.navigate([currentUrl]);
    });
  }
}
